import * as lodash from 'lodash';
import { DateTime } from 'luxon';
import { catchError, filter, of, switchMap, tap } from 'rxjs';

import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewContainerRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';

import { NgIconComponent } from '@ng-icons/core';

import { COLORS } from '@styles/colors';

import { InternalChartComponent } from '@cxc/ui-kit/chart.js/components/internal-chart';
import { decodeTimeline } from '@cxc/ui-kit/chart.js/components/timeline';
import { Timeline } from '@cxc/ui-kit/chart.js/components/timeline';
import { TimelineComponent } from '@cxc/ui-kit/chart.js/components/timeline';
import { ChartColorScheme } from '@cxc/ui-kit/chart.js/const-enums';
import { decodeChart } from '@cxc/ui-kit/chart.js/decode';
import { Chart } from '@cxc/ui-kit/chart.js/models';

import { CurrencyAndDate } from '@components/currency-and-date/currency-and-date.component';
import { fxExportLine } from '@components/currency-and-date/fx-disclosure';
import { EmptyStatePage } from '@components/empty-state-page/empty-state-page.component';
import { ExportReportModal } from '@components/export-report-modal/export-report-modal.component';
import { SelectableWidget } from '@components/selectable-widget/selectable-widget.component';
import { SelectionBanner } from '@components/selection-banner/selection-banner.component';
import { CurrencyAmountComponent } from '@cxc/ui-kit/components/currency-amount';
import {
  SummaryChartSkeleton,
  TimelineSkeleton,
} from '@cxc/ui-kit/skeletons/components';
import { CommitmentBreakdown } from '@pages/dashboard/portfolio/general-overview/components/commitment-breakdown/commitment-breakdown.component';
import { CoreMetrics } from '@pages/dashboard/portfolio/general-overview/components/core-metrics/core-metrics.component';
import { CutOffDate } from '@pages/dashboard/portfolio/general-overview/components/cut-off-date/cut-off-date.component';
import { Facilities } from '@pages/dashboard/portfolio/general-overview/components/facilities/facilities.component';
import { GraphsCarousel } from '@pages/dashboard/portfolio/general-overview/components/graphs-carousel/graphs-carousel.component';
import { WarningOverview } from '@pages/dashboard/portfolio/general-overview/components/warning-overview/warning-overview.component';

import { InjectAttributeDirective } from '@directives/inject-attribute.directive';

import { Insights } from '@models/operation/insights.model';
import {
  LENDER_POSITION_LABELS,
  type LenderPosition,
} from '@models/operation/lender-participation.model';

import { MatrixAmountPipe } from '@pages/dashboard/portfolio/general-overview/insights/pipes/matrix-amount.pipe';

import { AnalyticsLabelValuePipe, LoaderPipe } from '@cxc/ui-kit/pipes';
import { FromSnakeCasePipe } from '@pipes/from-snake-case/from-snake-case.pipe';
import { NameChangePipe } from '@pipes/name-change/name-change.pipe';
import { PercentFormatPipe } from '@pipes/percent-format/percent-format.pipe';

import { PendoService } from '@cxc/core/services';
import { LenderAPIMatrixService } from '@services/API/trusts/lender/matrix.service';
import { CurrencyState } from '@services/state/currency-state/currency.state';
import { CutOffDateState } from '@services/state/cut-off-date-state/cut-off-date.state';
import { LenderState } from '@services/state/lender-state/lender.state';
import { OverviewResumeState } from '@services/state/overview-resume-state/overview-resume.state';

import {
  downloadWidgetsAsPdf,
  downloadWidgetsAsPng,
  generateWidgetPreviews,
} from '@utils/export-widgets';

@Component({
  selector: 'general-overview-insights',
  standalone: true,
  imports: [
    NgIconComponent,
    ButtonModule,

    CoreMetrics,
    CommitmentBreakdown,
    Facilities,
    TimelineComponent,
    CurrencyAndDate,
    CutOffDate,
    EmptyStatePage,
    GraphsCarousel,
    InternalChartComponent,
    WarningOverview,
    SelectableWidget,
    SelectionBanner,
    ExportReportModal,
    TableModule,
    CurrencyAmountComponent,
    MatrixAmountPipe,
    FromSnakeCasePipe,
    NameChangePipe,
    PercentFormatPipe,
    LoaderPipe,
    TimelineSkeleton,
    SummaryChartSkeleton,
    SkeletonModule,
    InjectAttributeDirective,
    AnalyticsLabelValuePipe,
  ],
  templateUrl: './insights.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Page-scoped: the state must not outlive the Overview route, or it would
  // keep requesting overview-resume from the facility pages, which share
  // CutOffDateState and CurrencyState.
  providers: [OverviewResumeState],
})
export class GeneralOverviewInsights {
  // Angular Core
  destroyRef = inject(DestroyRef);
  viewContainerRef = inject(ViewContainerRef);
  private el = inject(ElementRef);
  private pendoService = inject(PendoService);

  // APIs
  lenderAPIMatrix = inject(LenderAPIMatrixService);

  // States
  cutOffDateState = inject(CutOffDateState);
  lenderState = inject(LenderState);
  currencyState = inject(CurrencyState);
  overviewResumeState = inject(OverviewResumeState);

  /**
   * Overview's stored figures are MXN (LEN-1808) absent a user selection.
   * Core Metrics and the Portfolio Characteristics money cells both render
   * through <currency-amount> in this currency (LEN-2464). The fallback
   * lives on CurrencyState, shared with the facility pages (MR !1265 S2).
   */
  displayCurrency = this.currencyState.displayCurrency;

  // LEN-2363: the commitment by priority position and by master servicer.
  coreMetrics = this.overviewResumeState.coreMetrics;

  /**
   * Loaded, and no facility in it (LEN-2365). Undefined metrics are "not
   * loaded yet", never empty.
   */
  isEmptyPortfolio = computed(
    () => !!this.coreMetrics() && !this.coreMetrics()!.operations?.length
  );

  positionLabel = (key: string | null): string =>
    key
      ? (LENDER_POSITION_LABELS[key as LenderPosition] ?? key)
      : 'Not reported';

  // Timeline
  overviewTimeline = signal<Timeline.Input | undefined>(undefined);

  // Matrix
  matrixDataTable = signal<Insights.Matrix[]>([]);

  // Charts
  firstChart = signal<Chart.Internal.Chart | undefined>(undefined);

  // Export
  exportGraphs = signal(false);
  exportModalVisible = signal(false);
  isExporting = signal(false);
  selectedWidgets = signal<HTMLElement[]>([]);
  previewItems = signal<string[]>([]);
  // pendingSelection, not selection: the loaded one lags behind the request, so
  // the export filename would pair the previous lender with the new cut-off.
  private lenderName = computed(
    () => this.overviewResumeState.pendingSelection()?.lender ?? ''
  );

  defaultExportFileName = computed(() => {
    const cutOff = this.cutOffDateState.currentPeriodISO();
    const date = cutOff ? DateTime.fromISO(cutOff).toFormat('d MMM yyyy') : '';
    return {
      lender: this.lenderName(),
      activeTab: 'Overview',
      date: date,
    };
  });

  constructor() {
    // setFromAPI is the page's job: GeneralOverviewPage is this component's
    // parent route, so it has always run by the time this constructor does
    // (LEN-2067).

    // Decode the slices this component owns. The request itself belongs to
    // OverviewResumeState, which the three sibling components share.
    // The undefined published on a selection change has to clear these two, or
    // the timeline and first chart keep rendering the previous lender under the
    // new header (LEN-2078).
    toObservable(this.overviewResumeState.resume)
      .pipe(takeUntilDestroyed())
      .subscribe(resume => {
        if (!resume) {
          this.overviewTimeline.set(undefined);
          this.firstChart.set(undefined);
          // The charts these figures came from are gone, so the factor that
          // converted them must go too — "first closing wins" would otherwise
          // keep the previous currency's rate in the tooltip after a switch.
          this.currencyState.clearAppliedRate();
          return;
        }
        // The tooltip and the export name the factor of the KPIs only: they
        // describe the whole lender, and the backend gives them an `fx` only
        // when every facility came from one currency (LEN-3020). A carousel
        // chart can cover a subset of the facilities, so its `fx` (e.g. COP→MXN
        // on an Aging where only the COP facility reports) would be shown as
        // the page's rate on a mixed lender. Without a KPI `fx` the tooltip
        // keeps its generic text (LEN-3022).
        this.currencyState.recordAppliedRate(resume.core_metrics?.fx);
        this.overviewTimeline.set(decodeTimeline(resume.overview));
        this.decodeFirstChart(resume.first_chart);
      });

    // Driven by pendingSelection so the export reset happens when the user
    // changes selection, not when the payload lands -- otherwise another
    // lender's widgets stay selected for the whole request.
    toObservable(this.overviewResumeState.pendingSelection)
      .pipe(
        filter(Boolean),
        tap(() => {
          this.exportGraphs.set(false);
          this.selectedWidgets.set([]);
          this.matrixDataTable.set([]);
        }),
        // If BBVA or CREDIT SAISON get Matrix
        filter(selection => selection.lender === 'BBVA SPARK'),
        switchMap(selection =>
          this.lenderAPIMatrix
            .getMatrix(selection.lender, selection.cutOff, selection.currency)
            .pipe(
              // Inside the switchMap: a failing matrix must not terminate the
              // outer subscription, which also owns the reset above.
              catchError(() => of([] as Insights.Matrix[]))
            )
        ),
        takeUntilDestroyed()
      )
      .subscribe(matrix => this.matrixDataTable.set(matrix));
  }

  toggleExportGraphs(): void {
    const next = !this.exportGraphs();
    this.exportGraphs.set(next);
    if (!next) {
      this.selectedWidgets.set([]);
    }
  }

  onWidgetToggle(checked: boolean, el: HTMLElement): void {
    if (checked) {
      this.selectedWidgets.update(arr => [...arr, el]);
    } else {
      this.selectedWidgets.update(arr => arr.filter(w => w !== el));
    }
  }

  isWidgetSelected(el: HTMLElement): boolean {
    return this.selectedWidgets().includes(el);
  }

  openExportModal(): void {
    this.previewItems.set(generateWidgetPreviews(this.selectedWidgets()));
    this.exportModalVisible.set(true);
  }

  async onExportDownload(event: {
    format: 'pdf' | 'png';
    fileName: string;
  }): Promise<void> {
    const name = event.fileName || 'overview-insights';
    const data = this.defaultExportFileName();
    const cutOff = this.cutOffDateState.currentPeriodISO();
    const currency = this.currencyState.currencySelected()?.currency;
    // LEN-1808 Escenario 5 / LEN-2092: when the figures were actually
    // converted, the export has to say what rate produced them - not just
    // which currency they're in. Read from `appliedRate`, the same signal the
    // tooltip reads: same code AND same data, so the printed factor is the
    // one the user saw on screen (MR !1207 B1 — `core_metrics.fx` directly
    // here could disagree with the tooltip until LEN-2126; it now feeds the
    // state instead, recorded first).
    const currencyLine = fxExportLine(
      currency,
      this.currencyState.appliedRate()
    );
    const exportOptions = {
      title: `${data.lender} \u2014 ${data.activeTab}`.toUpperCase(),
      subtitle: cutOff
        ? `Cut-off Date: ${DateTime.fromISO(cutOff).toFormat('dd/MM/yyyy')}`
        : '',
      currency: currencyLine,
    };
    this.pendoService.trackEvent('export_format_selected', {
      format: event.format,
    });
    this.isExporting.set(true);
    try {
      if (event.format === 'png') {
        await downloadWidgetsAsPng(this.selectedWidgets(), name, exportOptions);
      } else if (event.format === 'pdf') {
        await downloadWidgetsAsPdf(this.selectedWidgets(), name, exportOptions);
      }
      this.pendoService.trackEvent('export_success_download');
    } finally {
      this.isExporting.set(false);
      this.exportModalVisible.set(false);
      this.exportGraphs.set(false);
      this.selectedWidgets.set([]);
    }
  }

  selectAllWidgets(): void {
    const all = Array.from(
      this.el.nativeElement.querySelectorAll('[data-widget]')
    ) as HTMLElement[];
    this.selectedWidgets.set(all);
  }

  clearWidgets(): void {
    this.selectedWidgets.set([]);
  }

  decodeFirstChart(apiChart: Chart.V1.Chart) {
    const chartName = apiChart.title.replaceAll(' ', '_').toUpperCase();

    const chart = decodeChart(
      apiChart,
      this.destroyRef,
      this.viewContainerRef,
      {
        colors:
          chartName === 'OVERCOLLATERALIZATION'
            ? [COLORS.zinc, { 500: COLORS.blue[900] }]
            : ChartColorScheme['BORROWING_BASE'],
        // LEN-2465: fetched in the selected currency, so formatted in it too.
        currency: this.displayCurrency(),
        tooltip: {
          valueIsCurrency: chartName === 'OVERCOLLATERALIZATION' ? false : true,
          hideOnZeros: true,
          title: {
            autogenerate: true,
          },
        },
      }
    ) as Chart.Internal.Chart;

    switch (chartName) {
      case 'BORROWING_BASE':
        {
          const datasets = chart!.data.datasets.map(dataset => {
            if (dataset.label.toUpperCase() === 'BORROWING BASE') {
              return {
                ...dataset,
                fill: false,
                borderDash: [5, 5],
                tension: 0.4,
                borderWidth: 2,
              };
            }

            return {
              ...dataset,
            };
          });

          lodash.merge(chart, { data: { datasets: datasets } });

          this.firstChart.set(chart);
        }
        break;

      case 'OVERCOLLATERALIZATION':
        // Modifying data style
        chart.data.datasets = chart.data.datasets.map(dataset => {
          if (dataset.label.toUpperCase() === 'REQUIRED') {
            return {
              ...dataset,
              borderDash: [5, 5],
            };
          }

          return dataset;
        });

        this.firstChart.set(chart);
        break;

      default:
        break;
    }
  }
}
